import type {
  PharmaCompanyCreateRequest,
  PharmaCompanyDetailResponse,
} from '@/entities/pharma/api/pharma.api';

/** 레거시 목록 테이블과 동일한 계약 상태 라벨 */
export const getContractStatusLabel = (
  contractStartDate?: string,
  contractEndDate?: string,
): string => {
  if (!contractStartDate || !contractEndDate) return '-';

  const now = new Date();
  const start = new Date(contractStartDate);
  const end = new Date(contractEndDate);

  if (now >= start && now <= end) return '계약 중';
  if (now > end) return '계약 종료';
  if (now < start) return '계약 전';
  return '-';
};

export const formatCurrency = (amount?: number | null) =>
  amount != null ? `${amount.toLocaleString('ko-KR')}원` : '-';

/** 폼 빈 문자열 → undefined (선택 필드 API 전송용) */
export const emptyToUndefined = (value: string) =>
  value.trim() === '' ? undefined : value.trim();

/** API `filterInfo` — 레거시 Select와 동일하게 한글 값을 그대로 저장 */
export const FILTER_INFO_OPTIONS = [
  { value: '일반', label: '일반' },
  { value: '상세', label: '상세' },
] as const;

/** 매월 n일 — Select 옵션 (1일~31일) */
export const MONTH_DAY_OPTIONS = Array.from({ length: 31 }, (_, index) => {
  const day = index + 1;
  return { value: String(day), label: `${day}일` };
});

/**
 * 정산 완료일 전용 옵션 (−31일~31일, 0 제외).
 * 레거시 a715c35: 음수는 전월 기준으로 저장한다.
 * 서버 전송 값도 Select value 문자열 그대로 (`"-5"`, `"10"` 등).
 */
export const SETTLEMENT_COMPLETED_DAY_OPTIONS = Array.from(
  { length: 62 },
  (_, index) => {
    let dayValue = index - 31;
    if (dayValue >= 0) dayValue += 1;
    return { value: String(dayValue), label: `${dayValue}일` };
  },
);

/** API/폼에 저장된 일자 문자열을 Select value(1~31)로 정규화 */
export const normalizeMonthDay = (value?: string | null): string => {
  if (value == null || String(value).trim() === '') return '';
  const parsed = Number(String(value).trim());
  if (!Number.isFinite(parsed) || parsed < 1 || parsed > 31) return '';
  return String(parsed);
};

/** 정산 완료일: −31~−1, 1~31 (0 제외). 레거시와 동일하게 문자열 숫자 유지 */
export const normalizeSettlementCompletedDay = (
  value?: string | null,
): string => {
  if (value == null || String(value).trim() === '') return '';
  const parsed = Number(String(value).trim());
  if (
    !Number.isFinite(parsed) ||
    parsed === 0 ||
    parsed < -31 ||
    parsed > 31
  ) {
    return '';
  }
  return String(parsed);
};

/** API `taxInvoiceIssueMethod` — 레거시 Radio와 동일하게 한글 값을 그대로 저장 */
export const TAX_INVOICE_METHOD_OPTIONS = [
  { value: '일반', label: '일반' },
  { value: '역발행', label: '역발행' },
] as const;

/** API `originalSubmission` boolean — 레거시 Radio(true/false)와 동일 */
export const ORIGINAL_SUBMISSION_OPTIONS = [
  { value: 'true', label: '제출' },
  { value: 'false', label: '미제출' },
] as const;

export type OriginalSubmissionFormValue =
  | ''
  | (typeof ORIGINAL_SUBMISSION_OPTIONS)[number]['value'];

export const toOriginalSubmissionApi = (
  value: OriginalSubmissionFormValue,
): boolean | undefined => {
  if (value === 'true') return true;
  if (value === 'false') return false;
  return undefined;
};

export const fromOriginalSubmissionApi = (
  value: boolean | null | undefined,
): OriginalSubmissionFormValue => {
  if (value === true) return 'true';
  if (value === false) return 'false';
  return '';
};

export const toCreatePayload = (
  values: PharmaCompanyFormValues,
): PharmaCompanyCreateRequest => ({
  pharmaCompanyName: values.pharmaCompanyName.trim(),
  businessLicenseNumber: values.businessLicenseNumber.trim(),
  representativeName: values.representativeName.trim(),
  mainManagerPhone: values.mainManagerPhone.trim(),
  contractStatus: emptyToUndefined(values.contractStatus),
  contractStartDate: emptyToUndefined(values.contractStartDate),
  contractEndDate: emptyToUndefined(values.contractEndDate),
  filterInfo: emptyToUndefined(values.filterInfo),
  minPrescriptionAmount: values.minPrescriptionAmount
    ? Number(values.minPrescriptionAmount)
    : undefined,
  address: emptyToUndefined(values.address),
  mainManagerName: emptyToUndefined(values.mainManagerName),
  mainManagerEmail: emptyToUndefined(values.mainManagerEmail),
  mainManagerFaxNumber: emptyToUndefined(values.mainManagerFaxNumber),
  hospitalManagerName: emptyToUndefined(values.hospitalManagerName),
  hospitalManagerPhone: emptyToUndefined(values.hospitalManagerPhone),
  hospitalManagerEmail: emptyToUndefined(values.hospitalManagerEmail),
  hospitalManagerKakao: emptyToUndefined(values.hospitalManagerKakao),
  inventoryManagerName: emptyToUndefined(values.inventoryManagerName),
  inventoryManagerPhone: emptyToUndefined(values.inventoryManagerPhone),
  inventoryManagerEmail: emptyToUndefined(values.inventoryManagerEmail),
  inventoryManagerKakao: emptyToUndefined(values.inventoryManagerKakao),
  settlementManagerName: emptyToUndefined(values.settlementManagerName),
  settlementManagerPhone: emptyToUndefined(values.settlementManagerPhone),
  settlementManagerEmail: emptyToUndefined(values.settlementManagerEmail),
  settlementManagerKakao: emptyToUndefined(values.settlementManagerKakao),
  taxInvoiceManagerName: emptyToUndefined(values.taxInvoiceManagerName),
  taxInvoiceManagerPhone: emptyToUndefined(values.taxInvoiceManagerPhone),
  taxInvoiceManagerEmail: emptyToUndefined(values.taxInvoiceManagerEmail),
  taxInvoiceManagerKakao: emptyToUndefined(values.taxInvoiceManagerKakao),
  prescriptionDataRegisteredAt: emptyToUndefined(
    values.prescriptionDataRegisteredAt,
  ),
  settlementCompletedAt: emptyToUndefined(values.settlementCompletedAt),
  taxInvoiceIssueStandardDate: emptyToUndefined(
    values.taxInvoiceIssueStandardDate,
  ),
  taxInvoiceIssueMethod: emptyToUndefined(values.taxInvoiceIssueMethod),
  commissionPaymentDate: emptyToUndefined(values.commissionPaymentDate),
  commissionVerificationMethod: emptyToUndefined(
    values.commissionVerificationMethod,
  ),
  expenseReportBaseDate: emptyToUndefined(values.expenseReportBaseDate),
  settlementDataEntry: emptyToUndefined(values.settlementDataEntry),
  originalSubmission: toOriginalSubmissionApi(values.originalSubmission),
  originalSubmissionMethod: emptyToUndefined(values.originalSubmissionMethod),
  remarks: emptyToUndefined(values.remarks),
});

export interface PharmaCompanyFormValues {
  pharmaCompanyId: string;
  pharmaCompanyName: string;
  contractStatus: string;
  contractStartDate: string;
  contractEndDate: string;
  filterInfo: string;
  minPrescriptionAmount: string;
  businessLicenseNumber: string;
  representativeName: string;
  address: string;
  mainManagerName: string;
  mainManagerPhone: string;
  mainManagerEmail: string;
  mainManagerFaxNumber: string;
  hospitalManagerName: string;
  hospitalManagerPhone: string;
  hospitalManagerEmail: string;
  hospitalManagerKakao: string;
  inventoryManagerName: string;
  inventoryManagerPhone: string;
  inventoryManagerEmail: string;
  inventoryManagerKakao: string;
  settlementManagerName: string;
  settlementManagerPhone: string;
  settlementManagerEmail: string;
  settlementManagerKakao: string;
  taxInvoiceManagerName: string;
  taxInvoiceManagerPhone: string;
  taxInvoiceManagerEmail: string;
  taxInvoiceManagerKakao: string;
  /** 처방자료 등록일 (매월 n일) */
  prescriptionDataRegisteredAt: string;
  /** 정산 완료일 (매월 n일) */
  settlementCompletedAt: string;
  /** 세금계산서 작성 기준일 (매월 n일) */
  taxInvoiceIssueStandardDate: string;
  taxInvoiceIssueMethod: string;
  /** 수수료 지급일 (매월 n일) */
  commissionPaymentDate: string;
  commissionVerificationMethod: string;
  /** 지출보고서 작성 기준일 (매월 n일) */
  expenseReportBaseDate: string;
  settlementDataEntry: string;
  originalSubmission: OriginalSubmissionFormValue;
  originalSubmissionMethod: string;
  remarks: string;
}

export const defaultPharmaFormValues: PharmaCompanyFormValues = {
  pharmaCompanyId: '',
  pharmaCompanyName: '',
  contractStatus: '',
  contractStartDate: '',
  contractEndDate: '',
  filterInfo: '',
  minPrescriptionAmount: '',
  businessLicenseNumber: '',
  representativeName: '',
  address: '',
  mainManagerName: '',
  mainManagerPhone: '',
  mainManagerEmail: '',
  mainManagerFaxNumber: '',
  hospitalManagerName: '',
  hospitalManagerPhone: '',
  hospitalManagerEmail: '',
  hospitalManagerKakao: '',
  inventoryManagerName: '',
  inventoryManagerPhone: '',
  inventoryManagerEmail: '',
  inventoryManagerKakao: '',
  settlementManagerName: '',
  settlementManagerPhone: '',
  settlementManagerEmail: '',
  settlementManagerKakao: '',
  taxInvoiceManagerName: '',
  taxInvoiceManagerPhone: '',
  taxInvoiceManagerEmail: '',
  taxInvoiceManagerKakao: '',
  prescriptionDataRegisteredAt: '',
  settlementCompletedAt: '',
  taxInvoiceIssueStandardDate: '',
  taxInvoiceIssueMethod: '',
  commissionPaymentDate: '',
  commissionVerificationMethod: '',
  expenseReportBaseDate: '',
  settlementDataEntry: '',
  originalSubmission: '',
  originalSubmissionMethod: '',
  remarks: '',
};

export const detailToFormValues = (
  data: PharmaCompanyDetailResponse,
): PharmaCompanyFormValues => ({
  pharmaCompanyId: data.pharmaCompanyId != null ? String(data.pharmaCompanyId) : '',
  pharmaCompanyName: data.pharmaCompanyName ?? '',
  contractStatus: data.contractStatus ?? '',
  contractStartDate: data.contractStartDate ?? '',
  contractEndDate: data.contractEndDate ?? '',
  filterInfo: data.filterInfo ?? '',
  minPrescriptionAmount:
    data.minPrescriptionAmount != null
      ? String(data.minPrescriptionAmount)
      : '',
  businessLicenseNumber: data.businessLicenseNumber ?? '',
  representativeName: data.representativeName ?? '',
  address: data.address ?? '',
  mainManagerName: data.mainManagerName ?? '',
  mainManagerPhone: data.mainManagerPhone ?? '',
  mainManagerEmail: data.mainManagerEmail ?? '',
  mainManagerFaxNumber: data.mainManagerFaxNumber ?? '',
  hospitalManagerName: data.hospitalManagerName ?? '',
  hospitalManagerPhone: data.hospitalManagerPhone ?? '',
  hospitalManagerEmail: data.hospitalManagerEmail ?? '',
  hospitalManagerKakao: data.hospitalManagerKakao ?? '',
  inventoryManagerName: data.inventoryManagerName ?? '',
  inventoryManagerPhone: data.inventoryManagerPhone ?? '',
  inventoryManagerEmail: data.inventoryManagerEmail ?? '',
  inventoryManagerKakao: data.inventoryManagerKakao ?? '',
  settlementManagerName: data.settlementManagerName ?? '',
  settlementManagerPhone: data.settlementManagerPhone ?? '',
  settlementManagerEmail: data.settlementManagerEmail ?? '',
  settlementManagerKakao: data.settlementManagerKakao ?? '',
  taxInvoiceManagerName: data.taxInvoiceManagerName ?? '',
  taxInvoiceManagerPhone: data.taxInvoiceManagerPhone ?? '',
  taxInvoiceManagerEmail: data.taxInvoiceManagerEmail ?? '',
  taxInvoiceManagerKakao: data.taxInvoiceManagerKakao ?? '',
  prescriptionDataRegisteredAt: normalizeMonthDay(
    data.prescriptionDataRegisteredAt,
  ),
  settlementCompletedAt: normalizeSettlementCompletedDay(
    data.settlementCompletedAt,
  ),
  taxInvoiceIssueStandardDate: normalizeMonthDay(
    data.taxInvoiceIssueStandardDate,
  ),
  taxInvoiceIssueMethod: data.taxInvoiceIssueMethod ?? '',
  commissionPaymentDate: normalizeMonthDay(data.commissionPaymentDate),
  commissionVerificationMethod: data.commissionVerificationMethod ?? '',
  expenseReportBaseDate: normalizeMonthDay(data.expenseReportBaseDate),
  settlementDataEntry: data.settlementDataEntry ?? '',
  originalSubmission: fromOriginalSubmissionApi(data.originalSubmission),
  originalSubmissionMethod: data.originalSubmissionMethod ?? '',
  remarks: data.remarks ?? '',
});
